import { useCallback } from "react";
import { type QueryHookOptions } from "@apollo/client";
import { useVaultDocumentByRefQuery } from "#graphql/supergraphHooks";
import {
  type VaultDocumentByRefQuery,
  type VaultDocumentByRefQueryVariables,
} from "#graphql/supergraphTypes";

/** The document node as returned by `vaultDocumentByRef`, full version history included. */
export type VaultDocumentDetailNode = NonNullable<
  VaultDocumentByRefQuery["vaultDocumentByRef"]
>;

export interface UseVaultDocumentByRefReturn {
  /** The document node (server-shaped), or `null` while loading or when no document
   *  with this `publicRefId` exists. */
  node: VaultDocumentDetailNode | null;
  loading: boolean;
  error: ReturnType<typeof useVaultDocumentByRefQuery>["error"];
  /** Re-run the query. AWAITABLE — see `useVaultDocument` for why callers need this. */
  refetch: () => Promise<void>;
}

/**
 * DV-345 / DV-548 — document-detail route: fetch a single Vault document by
 * `publicRefId`, via the dedicated `vaultDocumentByRef` query rather than
 * scanning a `vaultDocumentsSearch` page. This is also the only path that
 * carries the document's full `versions` history (search only ever returns
 * `currentVersion`) — see `Document/mappers.ts`.
 */
export const useVaultDocumentByRef = (
  publicRefId: string,
  options?: Omit<
    QueryHookOptions<VaultDocumentByRefQuery, VaultDocumentByRefQueryVariables>,
    "variables"
  >,
): UseVaultDocumentByRefReturn => {
  const { data, loading, error, refetch } = useVaultDocumentByRefQuery({
    fetchPolicy: "no-cache",
    ...options,
    variables: { publicRefId },
  });

  const refetchDocument = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    node: data?.vaultDocumentByRef ?? null,
    loading,
    error,
    refetch: refetchDocument,
  };
};
